import { useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useMutation } from "@tanstack/react-query";

import dayjs from "dayjs";

import { InboundLayout, InboundHeaderCard } from "ui-components/Layout";
import {
  InboundSubmitButton,
  InboundDetailAddButton,
  InboundDetailEditButton,
} from "ui-components/Form";
import { Success, Error_ } from "ui-components/Alert";
import BasicTable2 from "ui-components/tables/BasicTable2";

import { headerRender, headerValues, headerValidation } from "./so-schema";
import { detailRender, detailValues, detailValidation } from "./so-details-schema";
import SO from "./components/SO";
import SODetail from "./components/SODetail";
import { soColumns } from "./columns";

import { useCustomerValue } from "providers/CustomerContext";
import { createSO } from "services/data.outbound.service";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import {
  skuValidation_,
  updateDetail,
  deleteDetail,
  getTotalQuantity,
} from "layouts/views/inbound/common/common";

const defaultValues = {
  ...headerValues,
  orderDate: dayjs(new Date()).format("YYYY-MM-DD"),
  status: "Open",
};

function AddSO() {
  const { customerCode } = useCustomerValue();
  const navigate = useNavigate();
  const formValues = { ...defaultValues, customerCode };
  const [details, setDetails] = useState([]);
  const [click, setClick] = useState(false)
  const [alert, setAlert] = useState(null);
  const onError = (data) => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    setAlert(<Error_ data={data} onChange={setAlert} />);
  };

  const form = useForm({
    defaultValues: formValues,
    values: formValues,
    resolver: yupResolver(headerValidation),
  });

  const {
    control,
    watch,
    formState: { errors },
    setValue,
    getValues,
    trigger,
  } = form;

  const [consigneeCode,shipmentType] = watch(["consigneeCode","shipmentType"]);

  const headerProps = {
    headerRender,
    customerCode,
    shipmentType,
    control,
    setValue,
    getValues,
    errors,
  };

  const detailProps = { details, setDetails, values: { customerCode, consigneeCode } };

  const createSOMutation = useMutation(createSO, {});

  const createStatus = createSOMutation.status === "loading";

  const onSubmit = async () => {
    setClick(true)
    const isValid = await trigger();
    if (!isValid) {
      window.scrollTo(0, 0);
      setClick(false)
      return;
    }

    if (details.length === 0) {
      setAlert(<Error_ content="Invalid SO Details" onChange={setAlert} />);
      setClick(false)
      return;
    }

    const data = getValues();

    const req = {
      ...data,
      soDetails: details.map((d) => ({ ...d, orderUOM: d.orderUOM.label, status: "O" })),
    };

    createSOMutation.mutate(req, {
      onSuccess: (data) => navigate("/outbound/so", { state: data.data.message }),
      onError,
    });
    setClick(false)
  };

  return (
    <InboundLayout lg={12}>
      {alert}
      <InboundHeaderCard>
        <form noValidate>
          <SO {...headerProps} />
        </form>
      </InboundHeaderCard>

      <Detail {...detailProps} />
      <InboundSubmitButton
        label={createStatus ? "Submit...":"Submit"}
        onClick={onSubmit}
        disabled={details.length === 0 || click || createStatus}
      />
    </InboundLayout>
  );
}

const Detail = ({ details, setDetails, values }) => {
  const [defaultVC, setDefaultVC] = useState(null);
  const columns = [
    {
      field: "actions",
      headerName: "Action",
      renderActions: ["Delete"],
      type: "actions",
    },
    ...soColumns,
  ];

  const [alert, setAlert] = useState(null);
  const [formValues, setFormValues] = useState(null);
  const [dialog, setDialog] = useState(null);

  const lineNo = formValues ? formValues.lineNo : details.length + 1;

  const form = useForm({
    defaultValues: { ...detailValues, lineNo },
    values: formValues || { ...detailValues, lineNo, vendorCode: defaultVC, status: "Open" },
    resolver: yupResolver(detailValidation),
  });

  const {
    control,
    watch,
    formState: { errors, isDirty },
    setValue,
    getValues,
    handleSubmit,
    reset,
  } = form;

  const [vendorCode, skuCode] = watch(["vendorCode", "skuCode"]);

  const detailProps = {
    detailRender,
    values: { ...values, vendorCode, skuCode },
    control,
    errors,
    setValue,
    getValues,
    setDefaultVC,
  };

  const onSubmit = (data) => {
    if (skuValidation_(details, data)) {
      setAlert(<Error_ content="Duplicate SKU Code" onChange={setAlert} />);
      return;
    }
    if (formValues === null) {
      setDetails([...details, { ...data }]);
      reset();
      setAlert(<Success content="Added" onChange={setAlert} />);
    } else {
      setDetails(updateDetail(details, data));
      setFormValues(null);
      setAlert(<Success content="Updated" onChange={setAlert} />);
    }
  };

  const onRowClick = (values) => {
    if (formValues !== null && isDirty) {
      setDialog(
        <InfoDialog
          title={`Unsaved changes for Line No ${formValues.lineNo}`}
          content="Do you want to save the changes"
          onOk={() => {
            const data = getValues();
            setDetails(updateDetail(details, data));
            setDialog(null);
            setFormValues(values);
          }}
          onClose={() => {
            setDialog(null);
            setFormValues(values);
          }}
        />
      );
    } else {
      setFormValues(values);
    }
    return true;
  };

  const onDelete = (data) => {
    setDetails(deleteDetail(details, data));
    setFormValues(null);
    setAlert(<Success content="Deleted" onChange={setAlert} />);
  };

  const totalOrderQuantity = getTotalQuantity(details, "orderQty");

  const totalQuantity = getTotalQuantity(details, "quantity");

  const caption = (
    <div>
      <span>Total Rows: {details.length}</span>
      <span style={{ marginLeft: "3rem" }}>Total Order Quantity: {totalOrderQuantity}</span>
      <span style={{ marginLeft: "3rem" }}>Total Quantity: {totalQuantity}</span>
    </div>
  );

  const tableProps = { statusLabel: "SO", columns, caption, rows: details, onRowClick, onDelete };

  return (
    <InboundHeaderCard>
      {alert}
      {dialog}
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <SODetail {...detailProps} />
        {formValues === null ? (
          <InboundDetailAddButton />
        ) : (
          <InboundDetailEditButton disabled={!isDirty} onClick={() => setFormValues(null)} />
        )}
      </form>
      <BasicTable2 {...tableProps} />
    </InboundHeaderCard>
  );
};

export default AddSO;
